/**
 * Helpers d'URL d'images responsives.
 *
 * Le backend sert chaque image de la médiathèque sous plusieurs largeurs via
 * `GET /api/files/<clé>?w=<largeur>` (voir backend/services/image_optimizer.py) :
 * la déclinaison WebP est produite au téléversement, ou fabriquée à la
 * première demande pour les images antérieures, puis mémorisée.
 *
 * Intérêt concret : sur une fiche produit, un téléphone télécharge une image
 * de 400 px (~30 Ko) au lieu du fichier plein format (~300 Ko). C'est le
 * levier principal des audits Lighthouse « Adaptez la taille des images » et
 * « Réduisez les temps de chargement » (LCP).
 *
 * Toutes les fonctions sont tolérantes : une URL externe (Unsplash, image
 * d'illustration d'un thème, miniature YouTube) ou vide traverse sans être
 * modifiée, et l'appelant obtient simplement une image classique.
 */

// DOIT rester synchronisé avec RESPONSIVE_WIDTHS dans
// backend/services/image_optimizer.py : le backend refuse toute autre
// largeur (liste blanche anti-déni de service) et renvoie alors l'original.
export const RESPONSIVE_WIDTHS = [400, 800, 1200, 1600];

const MANAGED_PATH = "/api/files/";

/** Vrai si l'URL est servie par notre backend, donc déclinable en variantes. */
export function isManagedImage(src) {
  return typeof src === "string" && src.includes(MANAGED_PATH);
}

/** Ajoute un paramètre de requête sans casser une URL qui en a déjà. */
function withParam(src, name, value) {
  if (!src || value === undefined || value === null || value === "") return src;
  return `${src}${src.includes("?") ? "&" : "?"}${name}=${encodeURIComponent(value)}`;
}

/**
 * URL d'une image, éventuellement à une largeur donnée.
 * `version` (facultatif) permet un cache navigateur d'un an côté backend :
 * l'URL change quand le contenu change.
 */
export function imageUrl(src, { width, version } = {}) {
  if (!isManagedImage(src)) return src;
  let url = src;
  if (width && RESPONSIVE_WIDTHS.includes(width)) url = withParam(url, "w", width);
  if (version) url = withParam(url, "v", version);
  return url;
}

/**
 * Attribut `srcSet` complet, ou une chaîne vide si l'image n'est pas gérée
 * par notre backend (le navigateur retombe alors sur `src`).
 *
 * On déclare toutes les largeurs même si l'image d'origine est plus petite :
 * le backend renvoie l'original quand la variante n'aurait aucun intérêt, si
 * bien qu'aucun descripteur ne pointe jamais vers une image manquante.
 */
export function buildSrcSet(src, widths = RESPONSIVE_WIDTHS, version) {
  if (!isManagedImage(src)) return "";
  return widths
    .filter((w) => RESPONSIVE_WIDTHS.includes(w))
    .map((w) => `${imageUrl(src, { width: w, version })} ${w}w`)
    .join(", ");
}

/**
 * Valeurs de `sizes` pour les gabarits récurrents du site. `sizes` décrit la
 * largeur d'AFFICHAGE de l'image, pas celle du fichier : sans lui le
 * navigateur suppose 100vw et télécharge systématiquement la plus grande
 * déclinaison — le srcset ne servirait alors à rien.
 */
export const SIZES = {
  // Grille catalogue : 2 colonnes sur mobile, 3 sur tablette, 4 au-delà.
  productGrid: "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
  // Visuel principal d'une fiche produit : pleine largeur sur mobile, moitié
  // d'écran en deux colonnes ensuite.
  productHero: "(max-width: 768px) 100vw, 50vw",
  // Vignettes (panier, résultats de recherche, galerie secondaire).
  thumbnail: "96px",
  // Vignettes de la médiathèque et du sélecteur d'images du back-office.
  mediaTile: "(max-width: 640px) 33vw, 200px",
};
